import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  InputGroup,
  InputLeftAddon,
  Select,
  Spinner,
  Text,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Chart, EmissionsDataChart, SuppliersEngagementChart } from "./components";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth, WithAuthorization } from "../../context";
import { Page } from "../../components/Page";
import { Card } from "../../components/Card";
import { H2 } from "components/text/headings";
import { useCalculateScenarioPlanning, useGetScenarioPlanningParameters } from "../../hooks/query";
import { generateArrayOfYears, QUESTIONNAIRE_STATUSES, showErrorToast, showWarnToast } from "../../utils";
import { isAllowedToView } from "utils/AuthUtils";
import { useGetEmissionsDataComparison } from "../../hooks/query/dashboard";
import { mapComparisonData, roundByLargestRemainderMethod } from "./helpers";
import { StatusCodes } from "http-status-codes";
import { useGetNumberOfSupplierQuestionnairesByStatus } from "../../hooks/query/reports";
import { useNavigate } from "react-router-dom";
import {
  companyEmissionsDashboardKey,
  netZeroStrategyKey,
  permissions,
  routes,
  supplierAdminDashboardKey,
} from "../../config";

function Dashboard() {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard",
  });
  const { t: globalTranslation } = useTranslation("translation");

  const [suppliersChartYear, setSuppliersChartYear] = useState((new Date().getFullYear() - 1).toString());
  const [suppliersEngagementData, setSuppliersEngagementData] = useState();
  const [chartData, setChartData] = useState();

  const toast = useToast();
  const { user } = useAuth0();
  const navigate = useNavigate();
  const { userPermissions } = useAuth();

  const {
    data: parameters,
    error: parametersError,
    isFetching: isParametersFetching,
    isSuccess,
  } = useGetScenarioPlanningParameters({
    onSuccess: (params) => {
      setPathwayId(params.pathwayId);
      setReductionRate(params.reductionRate);
      setGrowthRate(params.growthRate);
      setAverageCo2Price(params.averageCo2Price);
    },
    onError: (err) => handleErrors(err),
    cacheTime: 0,
  });

  const { data: comparisonData, error: comparisonError } = useGetEmissionsDataComparison({
    onError: (err) => handleErrors(err),
    cacheTime: 0,
  });

  const { data: allYearsEngagementData, error: suppliersEngagementError } =
    useGetNumberOfSupplierQuestionnairesByStatus("", {
      onError: (err) => handleErrors(err),
      cacheTime: 0,
    });

  useEffect(() => {
    if (allYearsEngagementData) {
      if (Object.keys(allYearsEngagementData).includes(suppliersChartYear)) {
        const selectedYearData = allYearsEngagementData[suppliersChartYear];
        setSuppliersEngagementData({
          newCount: selectedYearData.find((el) => el.status === QUESTIONNAIRE_STATUSES.NEW)?.count ?? 0,
          inProgressCount: selectedYearData.find((el) => el.status === QUESTIONNAIRE_STATUSES.IN_PROGRESS)?.count ?? 0,
          finalizedCount: selectedYearData.find((el) => el.status === QUESTIONNAIRE_STATUSES.FINALIZED)?.count ?? 0,
          totalCount: selectedYearData.reduce((n, { count }) => n + count, 0),
        });
      } else {
        setSuppliersEngagementData({
          newCount: 0,
          inProgressCount: 0,
          finalizedCount: 0,
          totalCount: 0,
        });
      }
    }
  }, [allYearsEngagementData, suppliersChartYear]);

  const [pathwayId, setPathwayId] = useState(parameters?.pathwayId);
  const [reductionRate, setReductionRate] = useState(parameters?.reductionRate ?? 50);
  const [growthRate, setGrowthRate] = useState(parameters?.growthRate ?? 10);
  const [averageCo2Price, setAverageCo2Price] = useState(parameters?.averageCo2Price ?? 100);

  const { isFetching: isCalculationFetching, error: calculateError } = useCalculateScenarioPlanning(
    {
      pathwayId,
      reductionRate,
      growthRate,
      averageCo2Price,
      enabled: isSuccess,
    },
    {
      onError: (err) => handleErrors(err),
      onSuccess: (response) => {
        setChartData(response.data);
        if (response.status === StatusCodes.PARTIAL_CONTENT) {
          showWarnToast(toast, response.data?.missingDataMessage + " " + globalTranslation("contactAdministrator"));
        }
      },
      cacheTime: 0,
    }
  );

  const handleErrors = (err) => {
    if (err.response?.status === 404) {
      return;
    }
    showErrorToast(toast, err.message);
  };

  const renderComparisonSection = () => {
    if (!comparisonError && comparisonData && comparisonData.length > 0) {
      const predefinedColors = {
        2021: "hsl(148, 48%, 43%)",
        2022: "hsl(43, 100%, 59%)",
        2023: "hsl(335, 79%, 50%)",
        2024: "hsl(167, 83%, 60%)",
      };
      return (
        <EmissionsDataChart
          data={mapComparisonData(comparisonData)}
          colors={predefinedColors}
          {...getWidgetProps(companyEmissionsDashboardKey)}
        />
      );
    }

    return (
      <>
        <Divider />
        <H2>{globalTranslation("noData")}</H2>
      </>
    );
  };

  const renderSuppliersEngagement = () => {
    if (
      !suppliersEngagementError &&
      suppliersEngagementData &&
      Object.keys(suppliersEngagementData).length > 0 &&
      suppliersEngagementData.totalCount > 0
    ) {
      const supplierQuestionnairesTypes = [
        {
          id: 1,
          title: t("suppliersEngagementChart.types.new"),
          key: "newCount",
          color: "hsl(209, 62%, 50%)",
        },
        {
          id: 2,
          title: t("suppliersEngagementChart.types.inProgress"),
          key: "inProgressCount",
          color: "hsl(24, 75%, 50%)",
        },
        {
          id: 3,
          title: t("suppliersEngagementChart.types.finalised"),
          key: "finalizedCount",
          color: "hsl(148, 48%, 43%)",
        },
      ];

      const mappedData = supplierQuestionnairesTypes
        .filter((el) => suppliersEngagementData[el.key] > 0)
        .map((el) => {
          return {
            ...el,
            value: suppliersEngagementData[el.key],
            percentageValue: (suppliersEngagementData[el.key] / suppliersEngagementData.totalCount) * 100,
          };
        });

      const roundedValues = roundByLargestRemainderMethod(mappedData.map((e) => e.percentageValue));
      mappedData.forEach((el, index) => {
        el.percentageValue = roundedValues[index];
      });

      return (
        <Flex {...getWidgetProps(supplierAdminDashboardKey)}>
          <Box width="50%" padding="4" height={"300px"}>
            <SuppliersEngagementChart data={mappedData} />
          </Box>
          <Flex flexFlow="column" width="50%" paddingInline={6} paddingBlock={2}>
            <Text borderBottom="1px solid black" padding="2" marginBottom={5} textAlign="center">
              {t("suppliersEngagementChart.totalSuppliers")}: {suppliersEngagementData.totalCount}
            </Text>
            <Box>
              {supplierQuestionnairesTypes.map((type) => {
                return (
                  <Flex key={type.key} gap={2} marginBottom={2}>
                    <Box width={5} height={5} backgroundColor={type.color} />
                    <Text>{type.title}</Text>
                  </Flex>
                );
              })}
            </Box>
          </Flex>
        </Flex>
      );
    }

    return (
      <>
        <Divider />
        <H2>{globalTranslation("noData")}</H2>
      </>
    );
  };

  const dynamicColSpan = useBreakpointValue({
    lg: 1,
    xl: 2,
  });

  const getNavigationHeaderProps = (routeKey, title) => {
    return isAllowedToView(userPermissions, permissions[routeKey] ?? [])
      ? {
          textDecoration: "underline",
          cursor: "pointer",
          title: title ?? "",
          onClick: () => navigate(routes[routeKey]),
        }
      : {};
  };

  const getWidgetProps = (routeKey) => {
    return isAllowedToView(userPermissions, permissions[routeKey] ?? [])
      ? {
          cursor: "pointer",
          onClick: () => navigate(routes[routeKey]),
        }
      : {};
  };

  return (
    <Page title={t("page.title", { name: user.name })} subTitle={t("page.subTitle")}>
      <Grid templateColumns={`repeat(${dynamicColSpan}, 1fr)`} gap="5" justifyContent="stretch">
        <GridItem colSpan={dynamicColSpan} maxWidth={"100%"}>
          <Card height={"100%"} paddingBottom={0}>
            <Flex direction="column" gap="6">
              <Heading
                size="md"
                {...getNavigationHeaderProps(companyEmissionsDashboardKey, t("emissionsDashboardTooltip"))}
              >
                {t("emissionsDataChart.title")}
              </Heading>
              {renderComparisonSection()}
            </Flex>
          </Card>
        </GridItem>
        <GridItem colSpan={1}>
          <Card height={"100%"} paddingBottom={0}>
            <Flex direction="column" gap="6">
              <Heading size="md" {...getNavigationHeaderProps(netZeroStrategyKey, t("scenarioImpactsTooltip"))}>
                {t("scenarioImpacts")}
              </Heading>
              {isParametersFetching || isCalculationFetching ? (
                <div style={{ textAlign: "center", marginTop: 20 }}>
                  <Spinner size="xl" />
                </div>
              ) : parametersError || calculateError?.response?.status === 404 ? (
                <>
                  <Divider />
                  <H2>{globalTranslation("noData")}</H2>
                </>
              ) : (
                <Chart data={chartData?.data} height="370" {...getWidgetProps(netZeroStrategyKey)} />
              )}
            </Flex>
          </Card>
        </GridItem>
        <GridItem colSpan={1}>
          <Card height={"100%"} paddingBottom={0}>
            <Flex direction="column" gap="6">
              <Flex justifyContent="space-between" alignItems="center">
                <Heading
                  size="md"
                  {...getNavigationHeaderProps(supplierAdminDashboardKey, t("supplierEngagementTooltip"))}
                >
                  {t("suppliersEngagementChart.title")}
                </Heading>
                <InputGroup display="flex" alignItems="center" width="200px">
                  <InputLeftAddon children={"Year"} fontWeight="semibold" />
                  <Select
                    backgroundColor="white"
                    borderRadius="none"
                    value={suppliersChartYear}
                    onChange={(e) => setSuppliersChartYear(e.target.value)}
                  >
                    {generateArrayOfYears().map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                </InputGroup>
              </Flex>
              {renderSuppliersEngagement()}
            </Flex>
          </Card>
        </GridItem>
      </Grid>
    </Page>
  );
}

export default WithAuthorization(Dashboard);
